import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Globe,
  Bug,
  AlertTriangle,
  Calendar,
  ArrowRight,
  Plus,
  Link2,
  Bell,
  ShieldCheck,
  TrendingDown,
  Crown,
  ChevronDown,
  FileCode,
  FileText,
} from 'lucide-react'
import { useDashboardStats, useCrawls, type Crawl } from '../hooks/useCrawls'
import {
  useSites,
  usePortfolioStats,
  useSiteAlerts,
  useAcknowledgeAlert,
  useSnoozeAlert,
  type Site,
  type SiteAlert,
} from '@/client/modules/site/hooks/useSites'
import { useGscConnections } from '../hooks/useGsc'
import { useGa4Properties } from '../hooks/useGa4'
import { scoreColor } from '@/client/lib/colors'

function formatDate(iso: string | null) {
  if (!iso) return '-'
  return new Date(iso).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatRelative(iso: string | null) {
  if (!iso) return 'Never'
  const d = new Date(iso)
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const days = Math.floor(diffMs / 86400000)
  if (days === 0) return 'Today'
  if (days === 1) return 'Yesterday'
  if (days < 7) return `${days}d ago`
  if (days < 30) return `${Math.floor(days / 7)}w ago`
  return `${Math.floor(days / 30)}mo ago`
}

function frequencyLabel(f: string | null) {
  if (!f) return null
  return { daily: 'Daily', weekly: 'Weekly', fortnightly: 'Fortnightly', monthly: 'Monthly' }[f] || f
}

function StatCard({
  label,
  value,
  subtitle,
  icon: Icon,
  loading,
  color,
  href,
}: {
  label: string
  value: number | string
  subtitle?: string
  icon: React.ComponentType<{ className?: string }>
  loading?: boolean
  color?: string
  href?: string
}) {
  const card = (
    <Card className={href ? 'hover:border-primary/50 transition-colors cursor-pointer' : undefined}>
      <CardContent className="flex items-center gap-4 py-4">
        <div className={`rounded-lg p-2.5 ${color || 'bg-primary/10'}`}>
          <Icon className={`h-5 w-5 ${color ? 'text-white' : 'text-primary'}`} />
        </div>
        <div>
          {loading ? (
            <Skeleton className="h-7 w-16 mb-1" />
          ) : (
            <p className="text-2xl font-bold">{typeof value === 'number' ? value.toLocaleString() : value}</p>
          )}
          <p className="text-xs text-muted-foreground">{label}</p>
          {subtitle && <p className="text-xs text-muted-foreground/70">{subtitle}</p>}
        </div>
      </CardContent>
    </Card>
  )
  if (href) {
    return <Link to={href}>{card}</Link>
  }
  return card
}

function TierBadge({ tier }: { tier: string }) {
  if (tier === 'managed') {
    return (
      <Badge variant="default" className="text-[10px] px-1.5 py-0 font-medium">
        <Crown className="h-2.5 w-2.5 mr-0.5" />
        Managed
      </Badge>
    )
  }
  return (
    <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-medium text-muted-foreground">
      Free
    </Badge>
  )
}

function ScoreBadge({ score }: { score: number | null }) {
  if (score === null) return <span className="text-xs text-muted-foreground">-</span>
  const rounded = Math.round(score)
  return <span className={`text-xs font-semibold ${scoreColor(rounded)}`}>{rounded}</span>
}

function RecentCrawlRow({ crawl }: { crawl: Crawl }) {
  const avgScore = crawl.pages_crawled > 0 ? Math.round(crawl.avg_seo_score ?? 0) : null
  // A "completed" crawl with 0 pages is a silent failure (target returned no crawlable HTML,
  // hit a redirect loop, or got blocked). Surface it as 'empty' rather than 'completed'.
  const effectiveStatus = crawl.status === 'completed' && crawl.pages_crawled === 0
    ? 'empty'
    : crawl.status

  return (
    <Link
      to={`/dashboard/crawls/${crawl.id}`}
      className="flex items-center justify-between rounded-lg px-3 py-2.5 text-sm hover:bg-accent transition-colors"
    >
      <div className="flex items-center gap-3 min-w-0">
        <Globe className="h-4 w-4 text-muted-foreground shrink-0" />
        <span className="font-medium truncate">{crawl.domain}</span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-xs text-muted-foreground">{crawl.pages_crawled} pg</span>
        <Badge
          variant={
            effectiveStatus === 'completed' ? 'outline' :
            effectiveStatus === 'running' ? 'default' :
            effectiveStatus === 'failed' || effectiveStatus === 'empty' ? 'destructive' : 'secondary'
          }
          className="text-xs"
          title={effectiveStatus === 'empty' ? 'Crawl completed but found 0 crawlable pages (likely blocked, redirect loop, or non-HTML response)' : undefined}
        >
          {effectiveStatus}
        </Badge>
        {avgScore !== null && avgScore > 0 && (
          <span className={`text-xs font-medium ${scoreColor(avgScore)}`}>
            {avgScore}
          </span>
        )}
      </div>
    </Link>
  )
}

function SiteRow({ site }: { site: Site }) {
  const freq = frequencyLabel(site.schedule_frequency)

  return (
    <Link
      to={`/dashboard/sites/${site.id}`}
      className="flex items-center justify-between rounded-lg px-3 py-2.5 text-sm hover:bg-accent transition-colors"
    >
      <div className="flex items-center gap-3 min-w-0">
        <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
        <span className="font-medium truncate">{site.domain}</span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {freq && <Badge variant="outline" className="text-xs">{freq}</Badge>}
        {site.schedule_next_run_at && (
          <span className="text-xs text-muted-foreground">
            Next: {formatDate(site.schedule_next_run_at)}
          </span>
        )}
      </div>
    </Link>
  )
}

function AlertRow({ alert, onAcknowledge, onSnooze }: { alert: SiteAlert; onAcknowledge: (id: string) => void; onSnooze: (alertId: string, days: number) => void }) {
  const summary = JSON.parse(alert.summary) as Record<string, unknown>

  const alertDescription = () => {
    switch (alert.alert_type) {
      case 'regression':
        return `+${summary['issuesDelta']} issues${summary['scoreChange'] !== null ? `, score ${(summary['scoreChange'] as number) > 0 ? '+' : ''}${summary['scoreChange']}` : ''}`
      case 'recovery':
        return 'Issues reduced, score improved'
      case 'page_change':
        return `${summary['changedPages']} page${(summary['changedPages'] as number) !== 1 ? 's' : ''} changed`
      case 'keyword_drop':
        return 'Keyword rankings dropped'
      default:
        return 'New issues detected'
    }
  }

  const borderColor = alert.alert_type === 'page_change' ? 'border-l-blue-500 bg-blue-500/5' : 'border-l-destructive bg-destructive/5'
  const iconColor = alert.alert_type === 'page_change' ? 'text-blue-600 dark:text-blue-400' : 'text-destructive'

  return (
    <div className={`flex items-center justify-between rounded-lg px-3 py-2.5 text-sm border-l-2 ${borderColor}`}>
      <div className="flex items-center gap-3 min-w-0">
        {alert.alert_type === 'page_change' ? (
          <FileText className={`h-4 w-4 ${iconColor} shrink-0`} />
        ) : (
          <TrendingDown className={`h-4 w-4 ${iconColor} shrink-0`} />
        )}
        <div className="min-w-0">
          <Link to={`/dashboard/crawls/${alert.crawl_id}`} className="font-medium truncate hover:underline block">
            {alert.domain}
          </Link>
          <p className="text-xs text-muted-foreground">
            {alertDescription()}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-xs text-muted-foreground">{formatRelative(alert.created_at)}</span>
        <Link to={`/dashboard/sites/${alert.site_id}`}>
          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs">
            View Site
          </Button>
        </Link>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={(e) => e.preventDefault()}>
              Dismiss <ChevronDown className="ml-1 h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onAcknowledge(alert.id)}>Dismiss</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onSnooze(alert.id, 7)}>Snooze 7 days</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onSnooze(alert.id, 0)}>Won't Fix</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )
}

export function DashboardPage() {
  const { data: stats, isLoading: statsLoading } = useDashboardStats()
  const { data: portfolio, isLoading: portfolioLoading } = usePortfolioStats()
  const { data: crawlsData, isLoading: crawlsLoading } = useCrawls(8)
  const { data: sitesData, isLoading: sitesLoading } = useSites()
  const { data: alertsData } = useSiteAlerts(false) // unacknowledged only
  const { data: gscData } = useGscConnections()
  const { data: ga4Data } = useGa4Properties()
  const acknowledgeAlert = useAcknowledgeAlert()
  const snoozeAlert = useSnoozeAlert()

  const recentCrawls = crawlsData?.crawls ?? []
  const totalCrawls = crawlsData?.totalCount ?? recentCrawls.length
  const scheduledSites = (sitesData?.sites ?? []).filter((s) => s.schedule_enabled)
  const gscConnections = gscData?.connections ?? []
  const ga4Properties = ga4Data?.properties ?? []
  const alerts = alertsData?.alerts ?? []
  const totalAlertCount = alertsData?.totalCount ?? alerts.length
  const pStats = portfolio?.stats

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-sm text-muted-foreground">Portfolio overview across all sites</p>
        </div>
        <div className="flex items-center gap-2">
          {totalAlertCount > 0 && (
            <Badge variant="destructive" className="text-xs">
              <Bell className="h-3 w-3 mr-1" />
              {totalAlertCount} alert{totalAlertCount !== 1 ? 's' : ''}
            </Badge>
          )}
          <Link to="/dashboard/crawls/new">
            <Button size="sm">
              <Plus className="mr-2 h-4 w-4" />
              New Crawl
            </Button>
          </Link>
        </div>
      </div>

      {/* Portfolio stats */}
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-6">
        <StatCard
          label="Total Sites"
          value={pStats?.total_sites ?? stats?.uniqueDomains ?? 0}
          subtitle={pStats ? `${pStats.managed_count} managed` : undefined}
          icon={Globe}
          loading={portfolioLoading && statsLoading}
        />
        <StatCard
          label="Managed Sites"
          value={pStats?.managed_count ?? 0}
          subtitle={pStats?.managed_avg_score ? `Avg: ${Math.round(pStats.managed_avg_score)}` : undefined}
          icon={Crown}
          loading={portfolioLoading}
          color="bg-blue-500"
        />
        <StatCard
          label="Critical Sites"
          value={pStats?.critical_sites ?? 0}
          subtitle="Score below 50"
          icon={AlertTriangle}
          loading={portfolioLoading}
          color={pStats?.critical_sites ? 'bg-red-500' : undefined}
          href="/dashboard/sites?filter=issues"
        />
        <StatCard
          label="Never Crawled"
          value={pStats?.never_crawled ?? 0}
          icon={Bug}
          loading={portfolioLoading}
          href="/dashboard/sites?filter=never-crawled"
        />
        {(pStats?.offline_sites ?? 0) > 0 && (
          <StatCard
            label="Offline/Unreachable"
            value={pStats?.offline_sites ?? 0}
            subtitle="Hosting error or down"
            icon={AlertTriangle}
            loading={portfolioLoading}
            color="bg-orange-500"
          />
        )}
        <StatCard
          label="Avg Score"
          value={pStats?.overall_avg_score ? Math.round(pStats.overall_avg_score) : '-'}
          subtitle="All crawled sites"
          icon={ShieldCheck}
          loading={portfolioLoading}
        />
        <StatCard
          label="Pages 2xx"
          value={pStats?.avg_health_score ? `${Math.round(pStats.avg_health_score)}%` : '-'}
          subtitle="Avg % of pages returning 200-299"
          icon={ShieldCheck}
          loading={portfolioLoading}
        />
      </div>

      {/* Alerts section */}
      {alerts.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Bell className="h-4 w-4 text-destructive" />
              Regression Alerts
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-1">
            {alerts.slice(0, 5).map((alert) => (
              <AlertRow
                key={alert.id}
                alert={alert}
                onAcknowledge={(id) => acknowledgeAlert.mutate(id)}
                onSnooze={(alertId, days) => snoozeAlert.mutate({ alertId, days })}
              />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Worst performers */}
      {portfolio && portfolio.worstPerformers.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Worst Performers</CardTitle>
              <Link to="/dashboard/sites" className="text-xs text-primary hover:underline flex items-center gap-1">
                All Sites <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-muted-foreground border-b">
                    <th className="text-left py-2 pr-4 font-medium">Domain</th>
                    <th className="text-center py-2 px-2 font-medium w-8"></th>
                    <th className="text-center py-2 px-2 font-medium">Tier</th>
                    <th className="text-center py-2 px-2 font-medium">Score</th>
                    <th className="text-center py-2 px-2 font-medium">Issues</th>
                    <th className="text-center py-2 px-2 font-medium">Pages</th>
                    <th className="text-right py-2 pl-2 font-medium">Last Crawl</th>
                  </tr>
                </thead>
                <tbody>
                  {portfolio.worstPerformers.slice(0, 10).map((site) => (
                    <tr key={site.id} className="border-b border-border/50 hover:bg-accent/50">
                      <td className="py-2 pr-4">
                        <Link to={`/dashboard/sites/${site.id}`} className="font-medium hover:underline truncate block max-w-[200px]">
                          {site.domain}
                        </Link>
                      </td>
                      <td className="py-2 px-2 text-center">
                        {!!site.is_wordpress && (
                          <span title="WordPress"><FileCode className="h-3.5 w-3.5 text-blue-500" /></span>
                        )}
                      </td>
                      <td className="py-2 px-2 text-center">
                        <TierBadge tier={site.tier} />
                      </td>
                      <td className="py-2 px-2 text-center">
                        <ScoreBadge score={site.latest_avg_seo_score} />
                      </td>
                      <td className="py-2 px-2 text-center text-xs">
                        {site.latest_issues_count ?? '-'}
                      </td>
                      <td className="py-2 px-2 text-center text-xs">
                        {site.latest_pages_crawled ?? '-'}
                      </td>
                      <td className="py-2 pl-2 text-right text-xs text-muted-foreground">
                        {formatRelative(site.latest_crawl_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Offline/Unreachable sites */}
      {portfolio && portfolio.offlineSites && portfolio.offlineSites.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Offline / Unreachable Sites</CardTitle>
            <p className="text-xs text-muted-foreground">
              These sites returned hosting errors (530, DNS failure, etc.) and couldn't be crawled properly. They are excluded from the worst performers list.
            </p>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-muted-foreground border-b">
                    <th className="text-left py-2 pr-4 font-medium">Domain</th>
                    <th className="text-center py-2 px-2 font-medium">Tier</th>
                    <th className="text-center py-2 px-2 font-medium">Pages</th>
                    <th className="text-right py-2 pl-2 font-medium">Last Attempt</th>
                  </tr>
                </thead>
                <tbody>
                  {portfolio.offlineSites.map((site) => (
                    <tr key={site.id} className="border-b border-border/50 hover:bg-accent/50">
                      <td className="py-2 pr-4">
                        <Link to={`/dashboard/sites/${site.id}`} className="font-medium hover:underline truncate block max-w-[200px]">
                          {site.domain}
                        </Link>
                      </td>
                      <td className="py-2 px-2 text-center">
                        <TierBadge tier={site.tier} />
                      </td>
                      <td className="py-2 px-2 text-center text-xs">
                        {site.latest_pages_crawled ?? 0}
                      </td>
                      <td className="py-2 pl-2 text-right text-xs text-muted-foreground">
                        {formatRelative(site.latest_crawl_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Recent crawls */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Recent Crawls</CardTitle>
              <Link to="/dashboard/crawls" className="text-xs text-primary hover:underline flex items-center gap-1">
                View all{totalCrawls > recentCrawls.length ? ` (${totalCrawls})` : ''} <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {crawlsLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : recentCrawls.length === 0 ? (
              <div className="py-8 text-center">
                <Bug className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">No crawls yet.</p>
                <Link to="/dashboard/crawls/new">
                  <Button size="sm" variant="outline" className="mt-3">
                    <Plus className="mr-2 h-4 w-4" />
                    Start Your First Crawl
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-0.5">
                {recentCrawls.map((crawl) => (
                  <RecentCrawlRow key={crawl.id} crawl={crawl} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Scheduled sites */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Scheduled Sites</CardTitle>
              <Link to="/dashboard/sites" className="text-xs text-primary hover:underline flex items-center gap-1">
                Manage <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {sitesLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : scheduledSites.length === 0 ? (
              <div className="py-8 text-center">
                <Calendar className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">No active schedules.</p>
                <Link to="/dashboard/sites">
                  <Button size="sm" variant="outline" className="mt-3">
                    Set Up a Schedule
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-0.5">
                {scheduledSites.slice(0, 6).map((site) => (
                  <SiteRow key={site.id} site={site} />
                ))}
                {scheduledSites.length > 6 && (
                  <Link to="/dashboard/sites" className="block text-xs text-primary hover:underline text-center pt-2">
                    +{scheduledSites.length - 6} more
                  </Link>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Google Accounts summary */}
      {gscConnections.length > 0 && (
        <Card>
          <CardContent className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-primary/10 p-2.5">
                <Link2 className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm font-medium">Integrations</p>
                <p className="text-xs text-muted-foreground">
                  {gscConnections.length} Google account{gscConnections.length !== 1 ? 's' : ''}
                  {ga4Properties.length > 0 && ` \u00B7 ${ga4Properties.length} GA4 properties`}
                </p>
              </div>
            </div>
            <Link to="/dashboard/integrations">
              <Button size="sm" variant="ghost">
                Manage <ArrowRight className="ml-1 h-3 w-3" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
